/**
 * One plan import, from the picked pictures to an opened project: the pictures are read as they are picked (so a bad
 * one says so at once), then uploaded together, the job is asked about until it settles, and its project is built
 * and stored as a new local project. The dialog only draws this state.
 *
 * Every run carries a token: a stop, a retry or a new pick takes it, and whatever the old run was still awaiting is
 * dropped when it arrives — the same guard the app keeps with its import generation.
 */
import {
  CLIENT_WAIT_CAP_MS, MAX_FLOORS, RecognitionError, type FailureCode, type FloorStatus, type JobStatus, type PreparedPlan,
  type RecognitionTransport,
} from './protocol';
import { t } from '../i18n/I18n';

export interface PlanEntry {
  id: string;
  name: string;
  size: number;
  state: 'reading' | 'ready' | 'failed';
  /** An object URL of the small copy, once read. */
  thumbnail: string | null;
  plan: PreparedPlan | null;
  error: FailureCode | null;
}

export interface RecognizedProject {
  projectId: string;
  name: string;
  score: number;
  floors: FloorStatus[];
  /** A weak result opens on the plan (`opensOnPlan`). */
  onPlan: boolean;
}

export type RecognitionPhase =
  | { kind: 'choosing' }
  | { kind: 'uploading'; fraction: number }
  | { kind: 'waiting'; job: JobStatus }
  | { kind: 'building'; done: number; total: number }
  | { kind: 'failed'; code: FailureCode; retryAfterMs: number | null; floors: FloorStatus[] }
  | { kind: 'ready'; project: RecognizedProject };

export interface RecognitionSnapshot {
  plans: PlanEntry[];
  phase: RecognitionPhase;
  /** When the upload began (`now()`), for the elapsed time; null while choosing. */
  startedAt: number | null;
  /** When the service took the job, for the estimated share of its work. */
  acceptedAt: number | null;
  /** More pictures were given than a project has floors; the extra ones were left out. */
  overflow: boolean;
  /** The stop is being confirmed. */
  stopping: boolean;
}

export interface RecognitionDeps {
  transport: RecognitionTransport;
  /** Reads a picked file (`preparePlan`). */
  prepare(file: File): Promise<{ plan: PreparedPlan; thumbnail: Blob }>;
  /** Builds the project out of the service's XML and stores it (`storeRecognized`). */
  build(xml: string, meta: { name: string; score: number; floors: FloorStatus[] }, onProgress: (done: number, total: number) => void): Promise<RecognizedProject>;
  culture: string;
  now?: () => number;
  wait?: (ms: number, signal: AbortSignal) => Promise<void>;
  urls?: { create(blob: Blob): string; revoke(url: string): void };
}

/** First question about a new job, then the usual pace; a refused question backs off. */
const FIRST_POLL_MS = 1000;
const POLL_MS = 1500;
const QUEUED_POLL_MS = 2500;
const MAX_POLL_FAILURES = 4;

const aborted = () => new DOMException('Aborted', 'AbortError');
const isAbort = (error: unknown) => error instanceof DOMException && error.name === 'AbortError';

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(aborted()); return; }
    const timer = setTimeout(() => { signal.removeEventListener('abort', stop); resolve(); }, ms);
    const stop = () => { clearTimeout(timer); reject(aborted()); };
    signal.addEventListener('abort', stop, { once: true });
  });
}

export class PlanRecognition {
  private state: RecognitionSnapshot = { plans: [], phase: { kind: 'choosing' }, startedAt: null, acceptedAt: null, overflow: false, stopping: false };
  private listeners = new Set<() => void>();
  private token = 0;
  private abort: AbortController | null = null;
  private jobId: string | null = null;
  private name = '';
  private sequence = 0;
  private readonly now: () => number;
  private readonly wait: (ms: number, signal: AbortSignal) => Promise<void>;
  private readonly urls: { create(blob: Blob): string; revoke(url: string): void };

  constructor(private readonly deps: RecognitionDeps) {
    this.now = deps.now ?? (() => Date.now());
    this.wait = deps.wait ?? sleep;
    this.urls = deps.urls ?? { create: blob => URL.createObjectURL(blob), revoke: url => URL.revokeObjectURL(url) };
  }

  get demo(): boolean { return this.deps.transport.demo; }
  get = (): RecognitionSnapshot => this.state;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  private set(patch: Partial<RecognitionSnapshot>) {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  private patchPlan(id: string, patch: Partial<PlanEntry>) {
    if (!this.state.plans.some(plan => plan.id === id)) {
      if (patch.thumbnail) this.urls.revoke(patch.thumbnail);
      return;
    }
    this.set({ plans: this.state.plans.map(plan => plan.id === id ? { ...plan, ...patch } : plan) });
  }

  /** Pictures picked or dropped; each is read at once. Only while choosing. */
  add(files: readonly File[]): void {
    if (this.state.phase.kind !== 'choosing' && this.state.phase.kind !== 'failed') return;
    const room = Math.max(0, MAX_FLOORS - this.state.plans.length);
    const taken = files.slice(0, room);
    const entries = taken.map((file): PlanEntry => ({ id: 'plan-' + ++this.sequence, name: file.name || t('#RecognitionPlan'), size: file.size,
      state: 'reading', thumbnail: null, plan: null, error: null }));
    this.set({ plans: [...this.state.plans, ...entries], overflow: files.length > room, phase: { kind: 'choosing' } });
    entries.forEach((entry, index) => {
      this.deps.prepare(taken[index]).then(({ plan, thumbnail }) => {
        this.patchPlan(entry.id, { state: 'ready', plan, thumbnail: this.urls.create(thumbnail) });
      }, error => {
        this.patchPlan(entry.id, { state: 'failed', error: error instanceof RecognitionError ? error.code : 'bad_image' });
      });
    });
  }

  remove(id: string): void {
    const plan = this.state.plans.find(item => item.id === id);
    if (!plan || this.busy) return;
    if (plan.thumbnail) this.urls.revoke(plan.thumbnail);
    this.set({ plans: this.state.plans.filter(item => item.id !== id), overflow: false });
  }

  /** Moves a plan a floor up (+1) or down (−1). */
  move(id: string, step: 1 | -1): void {
    const plans = [...this.state.plans], index = plans.findIndex(plan => plan.id === id), target = index + step;
    if (index < 0 || target < 0 || target >= plans.length || this.busy) return;
    [plans[index], plans[target]] = [plans[target], plans[index]];
    this.set({ plans });
  }

  get busy(): boolean {
    const kind = this.state.phase.kind;
    return kind === 'uploading' || kind === 'waiting' || kind === 'building';
  }

  get canStart(): boolean {
    return !this.busy && this.state.plans.length > 0 && this.state.plans.every(plan => plan.state === 'ready');
  }

  start(name: string): void {
    if (!this.canStart) return;
    this.name = name;
    void this.run(++this.token);
  }

  retry(): void {
    if (this.state.phase.kind === 'failed' && this.canStart) void this.run(++this.token);
  }

  /** The stop button: asks first, since a stopped job cannot be picked up again. */
  requestStop(): void { if (this.busy) this.set({ stopping: true }); }
  keepWaiting(): void { this.set({ stopping: false }); }

  /** Stops the run and keeps the pictures, back at the choice. */
  stop(): void {
    this.token++;
    this.abort?.abort();
    this.abort = null;
    if (this.jobId) this.deps.transport.cancel(this.jobId);
    this.jobId = null;
    this.set({ phase: { kind: 'choosing' }, startedAt: null, acceptedAt: null, stopping: false });
  }

  /** «Выбрать другое изображение»: the pictures go, the choice starts over. */
  clear(): void {
    this.stop();
    for (const plan of this.state.plans) if (plan.thumbnail) this.urls.revoke(plan.thumbnail);
    this.set({ plans: [], overflow: false });
  }

  /** The dialog is gone: the run stops and the pictures are let go. The instance stays usable (a remount). */
  dispose(): void { this.clear(); }

  private async run(token: number): Promise<void> {
    this.abort?.abort();
    const controller = this.abort = new AbortController();
    const signal = controller.signal;
    const current = () => token === this.token;
    const plans = this.state.plans.map(plan => plan.plan!);
    const deadline = this.now() + CLIENT_WAIT_CAP_MS;
    let job: JobStatus | null = null;
    this.set({ phase: { kind: 'uploading', fraction: 0 }, startedAt: this.now(), acceptedAt: null, stopping: false });
    try {
      job = await this.deps.transport.start({ floors: plans, culture: this.deps.culture }, fraction => {
        if (current() && this.state.phase.kind === 'uploading') this.set({ phase: { kind: 'uploading', fraction } });
      }, signal);
      this.jobId = job.id;
      if (current()) this.set({ acceptedAt: this.now() });
      let failures = 0, delay = FIRST_POLL_MS;
      while (job.state === 'queued' || job.state === 'running') {
        if (!current()) return;
        this.set({ phase: { kind: 'waiting', job } });
        if (this.now() >= deadline) {
          this.deps.transport.cancel(job.id);
          throw new RecognitionError('timed_out', 'The service did not finish in time');
        }
        await this.wait(job.retryAfterMs ?? delay, signal);
        try {
          job = await this.deps.transport.status(job.id, signal);
          failures = 0;
          delay = job.state === 'queued' ? QUEUED_POLL_MS : POLL_MS;
        } catch (error) {
          if (isAbort(error) || !(error instanceof RecognitionError) || !['network', 'busy', 'server'].includes(error.code)) throw error;
          if (++failures >= MAX_POLL_FAILURES) throw error;
          delay = error.retryAfterMs ?? POLL_MS * 2 ** failures;
        }
      }
      this.jobId = null;
      if (job.state === 'cancelled') throw new RecognitionError('cancelled');
      if (job.state === 'failed') throw new RecognitionError(job.error?.code ?? 'server', job.error?.message);
      if (!current()) return;
      this.set({ phase: { kind: 'building', done: 0, total: 0 } });
      const xml = await this.deps.transport.project(job, signal);
      if (!current()) return;
      const score = job.result?.score ?? 0;
      const project = await this.deps.build(xml, { name: this.name, score, floors: job.floors }, (done, total) => {
        if (current()) this.set({ phase: { kind: 'building', done, total } });
      });
      if (!current()) return;
      this.set({ phase: { kind: 'ready', project }, stopping: false });
    } catch (error) {
      if (!current() || isAbort(error)) return;
      // A job the web gave up on is not left running for nobody.
      if (this.jobId) this.deps.transport.cancel(this.jobId);
      this.jobId = null;
      const failure = error instanceof RecognitionError ? error : new RecognitionError('server', String(error));
      if (failure.code === 'cancelled') { this.set({ phase: { kind: 'choosing' }, startedAt: null, acceptedAt: null, stopping: false }); return; }
      this.set({ phase: { kind: 'failed', code: failure.code, retryAfterMs: failure.retryAfterMs ?? null, floors: job?.floors ?? [] }, stopping: false });
    } finally {
      if (this.abort === controller) this.abort = null;
    }
  }
}
